// Buyer-facing appointment booking modal — visible on /marketplace/store/{slug}
import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

function today() { return new Date().toISOString().slice(0, 10); }

export default function BookAppointmentModal({ store, onClose }) {
  const [date, setDate] = useState(today());
  const [mode, setMode] = useState('physical');
  const [paymentType, setPaymentType] = useState('part');
  const [notes, setNotes] = useState('');
  const [availability, setAvailability] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const checkAvail = useCallback(async () => {
    try {
      const r = await api.get(`/marketplace/store/${store.slug}/availability`, { params: { date } });
      setAvailability(r.data);
      const cfg = r.data.config || {};
      if (cfg.mode && cfg.mode !== 'both') setMode(cfg.mode);
    } catch {}
  }, [date, store.slug]);
  useEffect(() => { checkAvail(); }, [checkAvail]);

  const cfg = availability?.config || {};
  const price = Number(cfg.price_at || 0);
  const partPct = Number(cfg.part_payment_percent || 25);
  const amount = paymentType === 'full' ? price : Math.round(price * partPct) / 100;

  const book = async () => {
    if (!availability || availability.available <= 0) { toast.error('No slots available'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/marketplace/store/${store.slug}/book-appointment`, { date, mode, payment_type: paymentType, notes });
      setConfirmed(r.data.booking);
      toast.success('🎉 Appointment booked!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Booking failed');
    } finally { setBusy(false); }
  };

  if (confirmed) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border-2 border-emerald-400 rounded-2xl max-w-md w-full p-5 space-y-3 text-white" data-testid="booking-confirmed">
          <div className="text-center">
            <CheckCircle2 size={48} className="mx-auto text-emerald-400 mb-2" />
            <h2 className="text-xl font-black">Appointment Confirmed!</h2>
            <div className="text-xs opacity-70 mt-1">Booking ID: {confirmed.id.slice(0, 12)}</div>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-sm space-y-1">
            <div><b>Date:</b> {confirmed.date}</div>
            <div><b>Mode:</b> {confirmed.mode === 'virtual' ? '💻 Virtual Video Call' : '🏬 Physical Visit'}</div>
            <div><b>Paid:</b> {confirmed.amount_paid_at} AT</div>
            {confirmed.amount_remaining_at > 0 && <div><b>Due on visit:</b> {confirmed.amount_remaining_at} AT</div>}
          </div>
          <a href={confirmed.gcal_url} target="_blank" rel="noreferrer" data-testid="booking-gcal-add"
             className="block w-full py-2 rounded-lg bg-blue-500 text-white text-center text-xs font-black">
            📅 Add to Google Calendar
          </a>
          <a href={confirmed.ics_url} data-testid="booking-ics-download"
             className="block w-full py-2 rounded-lg bg-slate-700 text-white text-center text-xs font-black">
            📥 Download .ics (Apple/Outlook)
          </a>
          <button onClick={onClose} className="w-full py-2 rounded-lg bg-emerald-500 text-slate-900 font-black text-xs">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border-2 border-emerald-400 rounded-2xl max-w-md w-full p-5 space-y-3 text-white max-h-[92vh] overflow-y-auto" data-testid="booking-modal">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black flex items-center gap-2"><Calendar size={18} /> Book Appointment</h2>
          <button onClick={onClose} data-testid="booking-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="text-xs opacity-80">at <b>{store.shop_name}</b></div>

        <label className="block">
          <span className="text-[10px] font-black opacity-70">Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={today()} data-testid="booking-date"
                 className="w-full mt-0.5 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
        </label>

        {availability && availability.total_slots === 0 && (
          <div className="p-2 rounded bg-rose-900/40 border border-rose-500 text-xs">This shop is not accepting appointments yet</div>
        )}
        {availability && availability.total_slots > 0 && (
          <div className={`p-2 rounded text-xs ${availability.available > 0 ? 'bg-emerald-900/40 border border-emerald-500' : 'bg-rose-900/40 border border-rose-500'}`}>
            {availability.available > 0 ? `✅ ${availability.available} slot(s) available` : '❌ No slots left'}
          </div>
        )}

        {cfg.mode === 'both' && (
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setMode('physical')} data-testid="booking-mode-physical"
                    className={`p-2 rounded-lg text-xs font-black ${mode === 'physical' ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
              🏬 Physical Visit
            </button>
            <button onClick={() => setMode('virtual')} data-testid="booking-mode-virtual"
                    className={`p-2 rounded-lg text-xs font-black ${mode === 'virtual' ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
              💻 Virtual Video Call
            </button>
          </div>
        )}

        <div className="p-2 rounded bg-slate-800/50 space-y-1 text-xs">
          <div className="flex justify-between"><span>Full price:</span> <b>{price} AT</b></div>
          <div className="grid grid-cols-2 gap-1 pt-1">
            <button onClick={() => setPaymentType('part')} data-testid="booking-pay-part"
                    className={`p-1.5 rounded text-[10px] font-black ${paymentType === 'part' ? 'bg-yellow-400 text-slate-900' : 'bg-slate-700 text-slate-300'}`}>
              Part ({partPct}%): {Math.round(price * partPct) / 100} AT
            </button>
            <button onClick={() => setPaymentType('full')} data-testid="booking-pay-full"
                    className={`p-1.5 rounded text-[10px] font-black ${paymentType === 'full' ? 'bg-yellow-400 text-slate-900' : 'bg-slate-700 text-slate-300'}`}>
              Full: {price} AT
            </button>
          </div>
        </div>

        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)" data-testid="booking-notes"
                  className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />

        {/* Strict rules */}
        <div className="p-2 rounded-lg bg-amber-900/30 border border-amber-500 text-[10px] leading-relaxed" data-testid="booking-rules">
          <div className="font-black text-amber-300 flex items-center gap-1"><AlertTriangle size={11} /> Booking Rules — read carefully</div>
          <ul className="mt-1 space-y-0.5 opacity-90 list-disc list-inside">
            <li><b>Non-refundable</b> if you cancel or no-show</li>
            <li>Reschedule allowed <b>only once</b> — request shop owner first</li>
            <li>If shop cancels → <b>full auto-refund</b> to your AT wallet</li>
            <li>Amount will be deducted from your AT wallet immediately</li>
          </ul>
        </div>

        <button onClick={book} disabled={busy || !availability || availability.available <= 0 || price <= 0} data-testid="booking-confirm"
                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-900 font-black text-sm disabled:opacity-50">
          {busy ? '⏳ Booking…' : `Book & Pay ${amount} AT`}
        </button>
      </div>
    </div>
  );
}
